import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBuilding, FaSearch, FaMapMarkerAlt, FaPlus, FaPhoneAlt
} from 'react-icons/fa';
import './SheriffOffices.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Jails = () => {
  const [jails, setJails] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    counties: [],
    facilityTypes: [],
    operationalStatuses: []
  });
  const [filters, setFilters] = useState({
    search: '',
    county: '',
    facilityType: '',
    operationalStatus: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchFilterOptions();
    fetchJails();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchJails();
    }, 300);

    return () => clearTimeout(timer);
  }, [filters]);

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/jails/filters/options`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setFilterOptions(data.data);
      }
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchJails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Build query string
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.county) params.append('county', filters.county);
      if (filters.facilityType) params.append('facilityType', filters.facilityType);
      if (filters.operationalStatus) params.append('operationalStatus', filters.operationalStatus);

      const response = await fetch(`${API_URL}/jails?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setJails(data.data);
        setError('');
      } else {
        setError('Failed to load jails');
      }
    } catch (err) {
      console.error('Error fetching jails:', err);
      setError('Failed to load jails');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      county: '',
      facilityType: '',
      operationalStatus: ''
    });
  };

  const handleJailClick = (jailId) => {
    navigate(`/address-book/jails/${jailId}`);
  };

  const getFacilityTypeBadge = (facilityType) => {
    if (!facilityType) return null;

    const colors = {
      'County Jail': '#34495e',
      'Detention Center': '#7f8c8d',
      'Regional Facility': '#16a085',
      'Youth Detention': '#8e44ad',
      'Pre-Release Center': '#d68910',
      'Other': '#95a5a6'
    };

    const color = colors[facilityType] || '#7f8c8d';

    return (
      <span className="county-badge" style={{ background: color }}>
        {facilityType}
      </span>
    );
  };

  const getOperationalStatusBadge = (status) => {
    if (!status) return null;

    const colors = {
      'Operational': '#27ae60',
      'Limited Operations': '#f39c12',
      'Closed': '#e74c3c'
    };

    const color = colors[status] || '#7f8c8d';

    return (
      <span className="county-badge" style={{ background: color }}>
        {status}
      </span>
    );
  };

  if (loading && jails.length === 0) {
    return (
      <div className="sheriff-offices-container">
        <div className="loading">Loading jails and detention centers...</div>
      </div>
    );
  }

  return (
    <div className="sheriff-offices-container">
      {/* Header */}
      <div className="sheriff-offices-header">
        <div className="header-left">
          <FaBuilding size={32} style={{ color: '#34495e' }} />
          <div>
            <h1>Montana Jails & Detention Centers</h1>
            <p className="subtitle">{jails.length} facilities found</p>
          </div>
        </div>
        <button className="btn-add-office" style={{ background: '#34495e' }} onClick={() => navigate('/address-book/jails/new')}>
          <FaPlus /> Add Jail/Detention Center
        </button>
      </div>

      {/* Filters */}
      <div className="sheriff-offices-filters">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by facility name, county, or city..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label>County</label>
            <select
              value={filters.county}
              onChange={(e) => handleFilterChange('county', e.target.value)}
              className="filter-select"
            >
              <option value="">All Counties</option>
              {filterOptions.counties.map(county => (
                <option key={county} value={county}>{county}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Facility Type</label>
            <select
              value={filters.facilityType}
              onChange={(e) => handleFilterChange('facilityType', e.target.value)}
              className="filter-select"
            >
              <option value="">All Facility Types</option>
              {filterOptions.facilityTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Operational Status</label>
            <select
              value={filters.operationalStatus}
              onChange={(e) => handleFilterChange('operationalStatus', e.target.value)}
              className="filter-select"
            >
              <option value="">All Statuses</option>
              {filterOptions.operationalStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          {(filters.search || filters.county || filters.facilityType || filters.operationalStatus) && (
            <button className="btn-clear-filters" onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Jails List */}
      <div className="offices-list">
        {jails.length === 0 ? (
          <div className="no-results">
            <FaBuilding size={48} style={{ color: '#34495e' }} />
            <p>No jails or detention centers found matching your criteria</p>
          </div>
        ) : (
          jails.map(jail => (
            <div
              key={jail.id}
              className="office-card"
              onClick={() => handleJailClick(jail.id)}
              style={{ borderColor: '#f0f0f0' }}
            >
              <div className="office-header">
                <div className="office-title">
                  <FaBuilding className="office-icon" style={{ color: '#34495e' }} />
                  <h3>{jail.name || 'Unnamed Facility'}</h3>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  {jail.county && (
                    <span className="county-badge" style={{ background: '#34495e' }}>{jail.county} County</span>
                  )}
                  {getFacilityTypeBadge(jail.facility_type)}
                  {getOperationalStatusBadge(jail.operational_status)}
                </div>
              </div>

              {/* ORI Number - PROMINENT */}
              {jail.ori_number && (
                <div style={{
                  background: 'linear-gradient(135deg, #c0392b 0%, #e74c3c 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
                  border: '3px solid white'
                }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: '600', opacity: '0.9', marginBottom: '0.25rem', letterSpacing: '1px' }}>
                    ORI NUMBER
                  </div>
                  <div style={{ fontSize: '2rem', fontWeight: '700', letterSpacing: '2px', fontFamily: 'monospace' }}>
                    {jail.ori_number}
                  </div>
                </div>
              )}

              {/* Booking Phone - PROMINENT */}
              {jail.booking_phone && (
                <div style={{
                  background: 'linear-gradient(135deg, #2980b9 0%, #3498db 100%)',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  marginBottom: '15px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.15)',
                  border: '2px solid white'
                }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: '600', opacity: '0.9', marginBottom: '0.25rem', letterSpacing: '1px' }}>
                    BOOKING PHONE
                  </div>
                  <div style={{ fontSize: '1.75rem', fontWeight: '600', letterSpacing: '1px' }}>
                    {jail.booking_phone}
                  </div>
                </div>
              )}

              <div className="office-info">
                <div className="info-row">
                  {/* Capacity Badge */}
                  {jail.capacity && (
                    <div className="info-item">
                      <FaBuilding className="info-icon" style={{ color: '#34495e' }} />
                      <span>
                        <strong>Capacity:</strong> {jail.capacity}
                      </span>
                    </div>
                  )}

                  {/* Main Phone */}
                  {jail.phone && (
                    <div className="info-item">
                      <FaPhoneAlt className="info-icon" />
                      <span>
                        <strong>Main:</strong> {jail.phone}
                      </span>
                    </div>
                  )}

                  {/* Location */}
                  {(jail.city || jail.county) && (
                    <div className="info-item">
                      <FaMapMarkerAlt className="info-icon" />
                      <span>
                        {jail.city && `${jail.city}, `}
                        {jail.address_state || 'MT'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Address */}
                {jail.address_street && (
                  <div className="address-detail">
                    {jail.address_street}
                    {jail.city && `, ${jail.city}`}
                    {jail.address_state && `, ${jail.address_state}`}
                    {jail.address_zip && ` ${jail.address_zip}`}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Jails;
